import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import API from '../../../util/api';
import ConfirmationModal from '../../Common/ConfirmationModal';
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import { useLocation } from 'react-router-dom';
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";
import { showErrorModal } from "../../../util/general";

// ---- Styled Components ----

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: stretch;
`;

const Sidebar = styled.div`
  ${actionContainerStyles};
  align-self: flex-start;
  background: #fff;

  h3 {
    ${actionHeaderStyles};
    background: #2C4960;
    color: #fff;
    border-bottom: 1px solid ${({ colors }) => colors.border};
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 12px 16px;
  }
  li + li {
    margin-top: 4px;
  }
  a {
    ${actionLinkStyles};
    padding-left: 16px;
    color: #2C4960;
    &:hover {
      color: ${({ colors }) => colors.buttonBackground};
    }
  }
`;

const Main = styled.div`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
`;

const FilterBar = styled.div`
  margin-bottom: 1em;
  strong {
    margin-right: 0.5em;
  }
  select {
    padding: 4px;
  }
`;

const Table = styled.table`
  ${adminTable};
  font-size: 14px;

  thead th {
    ${adminTableHeaderCell};
  }

  tbody td {
    ${adminTableDataCell};
  }
`;

const ActionButton = styled.button`
  background: ${(p) => p.colors.buttonBackground};
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  margin-right: 4px;
  cursor: pointer;
  font-size: 0.85em;

  &:hover {
    opacity: 0.9;
  }
`;

const Bigbox = styled.div`
  background: #fff;
  padding: 1em;
  border-radius: 4px;

  h1 {
    margin-top: 0;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1em;
  }

  label {
    font-weight: bold;
  }

  input[type='file'] {
    padding: 0.5em;
  }

  textarea,
  select {
    width: 100%;
    padding: 0.5em;
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  .submit input {
    padding: 0.5em 1em;
    background: ${(p) => p.colors.buttonBackground};
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }

  a.cancel {
    margin-top: 0.5em;
    color: #999;
    cursor: pointer;
    &:hover {
      color: #666;
    }
  }
`;

// ---- Component ----

export default function ResourcesContent({ colors }) {
  const location = useLocation();
  const locationMode = location.state?.mode;
  const [mode, setMode] = useState('list'); // 'list' or 'upload'
  const [filterType, setFilterType] = useState('video');
  const [resources, setResources] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);

  useEffect(() => {
    setMode(locationMode || 'list');
  }, [location.key, locationMode]);

  const [uploadForm, setUploadForm] = useState({
    file: null,
    description: '',
    type: 'document',
  });

  // Fetch resources whenever mode switches to 'list' or filter changes
  useEffect(() => {
    if (mode !== 'list') return;

    API.getResources({ type: filterType })
      .then((resp) => {
        setResources(resp.result || []);
      })
      .catch((err) => {
        console.error('Failed to load resources:', err);
        setResources([]);
      });
  }, [filterType, mode]);

  // Handle “Add New Resource” form submit → show confirmation
  const handleSubmit = (e) => {
    e.preventDefault();
    setConfirmOpen(true);
  };

  // After confirming upload: read file → extract filename/extension → send
  const doUpload = async () => {
    setConfirmOpen(false);

    if (!uploadForm.file) {
      showErrorModal('Please select a file.');
      return;
    }

    const file = uploadForm.file;
    const fullName = file.name; // e.g. "example.pdf"
    const lastDotIdx = fullName.lastIndexOf('.');
    const filename = lastDotIdx > 0 ? fullName.slice(0, lastDotIdx) : fullName;
    const extension = lastDotIdx > 0 ? fullName.slice(lastDotIdx + 1).toLowerCase() : '';

    const reader = new FileReader();
    reader.onload = async () => {
      const base64String = reader.result.split(',')[1];

      const payload = {
        resource_name: filename,
        file_base64: base64String,
        filename,
        extension,
        description: uploadForm.description,
        type: uploadForm.type,
      };

      try {
        const resp = await API.addResources(payload);
        if (resp.status === 'Success') {
          setUploadForm({
            file: null,
            description: '',
            type: 'document',
          });
          setMode('list');
        } else {
          showErrorModal('Upload failed: ' + (resp.message || 'Unknown error'));
        }
      } catch (err) {
        showErrorModal('Upload failed: ' + err.message);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleDeleteClick = (resourceId) => {
    setResourceToDelete(resourceId);
    setConfirmDeleteOpen(true);
  };

  const doDelete = async () => {
    if (!resourceToDelete) return;
    setConfirmDeleteOpen(false);

    try {
      const resp = await API.deleteResources({ resource_id: resourceToDelete });
      if (resp.status === 'Success') {
        setResources((prev) =>
          prev.filter((r) => r.resource_id !== resourceToDelete.toString())
        );
        setResourceToDelete(null);
      } else {
        showErrorModal('Delete failed: ' + (resp.message || 'Unknown error'));
      }
    } catch (err) {
      showErrorModal('Delete failed: ' + err.message);
    }
  };

  const handleDownload = (r) => {
    const link = document.createElement('a');
    link.href = r.file;
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container>
      {/* -- Admin Actions Sidebar -- */}
      <ResponsiveActions
        colors={colors}
        renderActions={() => (
          <Sidebar colors={colors}>
            <h3>Admin Actions</h3>
            <ul>
              {mode === 'list' ? (
                <li>
                  <a onClick={() => setMode('upload')}>Add New Resource</a>
                </li>
              ) : (
                <li>
                  <a onClick={() => setMode('list')}>Back to Resources</a>
                </li>
              )}
            </ul>
          </Sidebar>
        )}
      />

      {/* -- Main Content Area -- */}
      <Main>
        {mode === 'list' && (
          <>
            <FilterBar>
              <strong>Filter:</strong>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="audio">Audio</option>
                <option value="document">Document</option>
                <option value="image">Image</option>
                <option value="package">Package</option>
                <option value="video">Video</option>
              </select>
            </FilterBar>

            <Table>
              <thead>
                <tr>
                  <th>Resource</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Additional Actions</th>
                </tr>
              </thead>
              <tbody>
                {resources.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      There are no resources available at this time.
                    </td>
                  </tr>
                ) : (
                  resources?.map((r) => (
                    <tr key={r.resource_id}>
                      <td>{r.resource_name}</td>
                      <td>{r.type}</td>
                      <td>{r.resource_description || '\u2014'}</td>
                      <td>
                        <ActionButton
                          colors={colors}
                          style={{marginBottom: '10px'}}
                          onClick={() => handleDownload(r)}
                        >
                          Download
                        </ActionButton>
                        <ActionButton
                          colors={colors}
                          onClick={() => handleDeleteClick(r.resource_id)}
                        >
                          Delete
                        </ActionButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </>
        )}

        {mode === 'upload' && (
          <Bigbox colors={colors}>
            <h1>Upload A New File</h1>
            <strong>Maximum File Size for Upload: 50M</strong>
            <form onSubmit={handleSubmit}>
              <div>
                <label htmlFor="file">Select file to upload</label>
                <input
                  type="file"
                  id="file"
                  onChange={(e) =>
                    setUploadForm((f) => ({ ...f, file: e.target.files[0] }))
                  }
                  required
                />
              </div>

              <div>
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  rows={3}
                  value={uploadForm.description}
                  onChange={(e) =>
                    setUploadForm((f) => ({ ...f, description: e.target.value }))
                  }
                />
              </div>

              <div>
                <label htmlFor="type">Type</label>
                <select
                  id="type"
                  value={uploadForm.type}
                  onChange={(e) =>
                    setUploadForm((f) => ({ ...f, type: e.target.value }))
                  }
                >
                  <option value="audio">Audio</option>
                  <option value="document">Document</option>
                  <option value="image">Image</option>
                  <option value="package">Package</option>
                  <option value="video">Video</option>
                </select>
              </div>

              <div className="submit">
                <input type="submit" value="Submit Resource" />
              </div>
            </form>

            <a className="cancel" onClick={() => setMode('list')}>
              Cancel Upload
            </a>
          </Bigbox>
        )}

        {/* Upload Confirmation Modal */}
        <ConfirmationModal
          show={confirmOpen}
          title="Confirm Upload"
          message="Are you sure you want to submit this resource?"
          onCancel={() => setConfirmOpen(false)}
          onConfirm={doUpload}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          show={confirmDeleteOpen}
          title="Confirm Delete"
          message="Are you sure you want to delete this resource?"
          onCancel={() => {
            setConfirmDeleteOpen(false);
            setResourceToDelete(null);
          }}
          onConfirm={doDelete}
        />
      </Main>
    </Container>
  );
}
